import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { showToast } from '../../../../components'
import { CustomDatePicker } from '../../../../components/CustomDatePicker'
import { AttendanceDetailsDrawer } from '../components/AttendanceDetailsDrawer'
import { AttendanceLeaveTabs } from '../components/AttendanceLeaveTabs'
import { AttendanceLogDrawer } from '../components/AttendanceLogDrawer'
import { AttendanceOverview } from '../components/AttendanceOverview'
import { AttendanceTab } from '../components/AttendanceTab'
import { ConfigurationContextBadge } from '../components/ConfigurationContextBadge'
import { ConfigurationTab } from '../components/ConfigurationTab'
import { ConfigurationTabHeader } from '../components/ConfigurationTabHeader'
import { LeaveRequestDrawer } from '../components/LeaveRequestDrawer'
import { LeaveTab } from '../components/LeaveTab'
import { LeaveTabHeader } from '../components/LeaveTabHeader'
import type { ConfigSubTab } from '../hooks/useAttendanceSettings'
import { useHrmsAttendanceLeave } from '../hooks/useHrmsAttendanceLeave'
import type { AttendanceRecord, TabKey } from '../types/hrmsAttendanceLeave.types'
import type { StaffAttendanceRecord } from '../types/hrmsAttendanceRecords.types'
import type { StaffLeaveRequest, StaffLeaveType } from '../types/hrmsLeaveRequests.types'

export default function HRMSAttendanceLeavePage() {
  const location = useLocation()
  const navigate = useNavigate()

  const [logDrawerRecord, setLogDrawerRecord] = useState<AttendanceRecord | null>(null)
  const [reviewRegularizationRecord, setReviewRegularizationRecord] = useState<StaffAttendanceRecord | null>(null)
  const [reviewLeaveRequest, setReviewLeaveRequest] = useState<StaffLeaveRequest | null>(null)

  const getTabFromLocation = (): TabKey => {
    const path = location.pathname
    if (path.endsWith('/attendance')) return 'attendance'
    if (path.endsWith('/leave')) return 'leave'
    if (path.includes('/configuration')) return 'configuration'
    return 'overview'
  }

  const selectedTab = getTabFromLocation()

  const getConfigSubTabFromLocation = (): ConfigSubTab => {
    if (location.pathname.endsWith('/leave-policies')) {
      return 'leave-policies'
    }
    return 'attendance-settings'
  }

  const configSubTab = getConfigSubTabFromLocation()

  const handleTabChange = (tab: TabKey) => {
    if (tab === 'configuration') {
      navigate('/admin/hrms/attendance-leave/configuration/attendance-settings')
    } else {
      navigate(`/admin/hrms/attendance-leave/${tab}`)
    }
  }

  const handleSubTabChange = (subTab: ConfigSubTab) => {
    navigate(`/admin/hrms/attendance-leave/configuration/${subTab}`)
  }

  const {
    selectedDate,
    setSelectedDate,
    departmentOptions,
    summary,
    loadingSummary,
    todayRecords,
    todayTotal,
    todayPage,
    todayLimit,
    loadingToday,
    todaySearch,
    todayDepartmentId,
    todayStatus,
    handleTodaySearchChange,
    handleTodayDepartmentChange,
    handleTodayStatusChange,
    setTodayPage,
    pendingRequests,
    pendingTotal,
    pendingPage,
    pendingLimit,
    loadingPending,
    pendingSearch,
    pendingDepartmentId,
    pendingRequestType,
    handlePendingSearchChange,
    handlePendingDepartmentChange,
    handlePendingRequestTypeChange,
    setPendingPage,
    removePendingRequest,
    loading,
    filteredAttendance,
    filteredPendingRequests,
    selectedDepartment,
    setSelectedDepartment,
  } = useHrmsAttendanceLeave()

  const handleViewAttendanceClick = (id: string) => {
    const rec = filteredAttendance.find((a) => a.id === id) || null
    setLogDrawerRecord(rec)
  }

  const handleReviewRequestClick = (id: string) => {
    const req = filteredPendingRequests.find((r) => r.id === id)
    if (!req) return
    if (req.requestType === 'Regularization') {
      setReviewRegularizationRecord({
        id: req.id,
        name: req.name,
        designation: 'Faculty',
        employeeId: req.id.startsWith('EMP') ? req.id : 'EMP-1028',
        department: req.department,
        checkIn: '09:02',
        checkOut: '17:58',
        workingMinutes: 536,
        status: 'Late',
        source: 'Biometric',
      })
    } else if (req.requestType === 'Leave Request') {
      setReviewLeaveRequest({
        id: req.id,
        name: req.name,
        employeeId: req.id.startsWith('EMP') ? req.id : 'EMP-1028',
        department: req.department,
        leaveType: (req.title as StaffLeaveType) || 'Casual Leave',
        from: '2026-09-24',
        to: '2026-09-25',
        status: 'Pending',
      })
    }
  }

  const handleApproveLeave = (id: string) => {
    removePendingRequest(id)
    setReviewLeaveRequest(null)
    showToast({
      type: 'success',
      title: 'Success',
      message: 'Leave request approved successfully',
    })
  }

  const handleRejectLeave = (id: string) => {
    removePendingRequest(id)
    setReviewLeaveRequest(null)
    showToast({
      type: 'success',
      title: 'Success',
      message: 'Leave request rejected successfully',
    })
  }

  const handleRegularizationHandled = (requestId: string) => {
    removePendingRequest(requestId)
    setReviewRegularizationRecord(null)
  }

  if (loading && !summary) {
    return (
      <div className="flex h-64 items-center justify-center p-6 text-slate-500">
        Loading Attendance &amp; Leave...
      </div>
    )
  }

  const handleNoop = () => {}

  return (
    <div className="w-full max-w-full min-w-0 min-h-full bg-[#f8fafc] text-[#131927] font-sans py-6">
      <div className="w-full max-w-[1600px] min-w-0 mx-auto space-y-6">
        {/* Header */}
        {selectedTab === 'leave' ? (
          <LeaveTabHeader />
        ) : selectedTab === 'configuration' ? (
          <ConfigurationTabHeader />
        ) : selectedTab === 'overview' ? (
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900">Attendance &amp; Leave</h1>
                <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-semibold text-[#146971] border border-teal-200">
                  Academic Session 2026-27
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Monitor today's attendance, leave status, and pending requests.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 min-w-0 shrink-0">
              <div className="shrink-0 w-[190px]">
                <CustomDatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  placeholder="Select date"
                  buttonClassName="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 shadow-xs hover:border-slate-300 focus:outline-none font-medium"
                />
              </div>
            </div>
          </div>
        ) : null}

        {/* Tab Bar (for overview, leave, and configuration tabs) */}
        {selectedTab !== 'attendance' && (
          <div className="flex items-center justify-between gap-4 flex-wrap min-w-0">
            <AttendanceLeaveTabs activeTab={selectedTab} onTabChange={handleTabChange} />
            {selectedTab === 'configuration' && configSubTab === 'attendance-settings' && (
              <ConfigurationContextBadge />
            )}
          </div>
        )}

        {/* Tab Content */}
        <div className="min-w-0">
          {selectedTab === 'overview' && summary ? (
            <AttendanceOverview
              summary={summary}
              attendanceRecords={todayRecords}
              todayTotal={todayTotal}
              todayPage={todayPage}
              todayLimit={todayLimit}
              todayLoading={loadingToday}
              todaySearch={todaySearch}
              onTodaySearchChange={handleTodaySearchChange}
              todayDepartmentId={todayDepartmentId}
              onTodayDepartmentChange={handleTodayDepartmentChange}
              departmentOptions={departmentOptions}
              todayStatus={todayStatus}
              onTodayStatusChange={handleTodayStatusChange}
              onTodayPageChange={setTodayPage}

              pendingRequests={pendingRequests}
              pendingTotal={pendingTotal}
              pendingPage={pendingPage}
              pendingLimit={pendingLimit}
              pendingLoading={loadingPending}
              pendingSearch={pendingSearch}
              onPendingSearchChange={handlePendingSearchChange}
              pendingDepartmentId={pendingDepartmentId}
              onPendingDepartmentChange={handlePendingDepartmentChange}
              pendingRequestType={pendingRequestType}
              onPendingRequestTypeChange={handlePendingRequestTypeChange}
              onPendingPageChange={setPendingPage}

              onSwitchTab={handleTabChange}
              onViewAttendance={handleViewAttendanceClick}
              onReviewRequest={handleReviewRequestClick}
            />
          ) : selectedTab === 'attendance' ? (
            <AttendanceTab
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              selectedDepartment={selectedDepartment}
              onDepartmentChange={setSelectedDepartment}
              tabSlot={
                <div className="flex items-center justify-between gap-4 flex-wrap min-w-0">
                  <AttendanceLeaveTabs activeTab={selectedTab} onTabChange={handleTabChange} />
                </div>
              }
            />
          ) : selectedTab === 'leave' ? (
            <LeaveTab
              onReviewLeave={handleNoop}
              onViewLeave={handleNoop}
            />
          ) : (
            <ConfigurationTab
              activeSubTab={configSubTab}
              onSubTabChange={handleSubTabChange}
              onAddLeavePolicy={handleNoop}
              onEditLeavePolicy={handleNoop}
            />
          )}
        </div>
      </div>

      {/* Drawers */}
      <AttendanceLogDrawer
        record={logDrawerRecord}
        onClose={() => setLogDrawerRecord(null)}
      />

      <AttendanceDetailsDrawer
        record={reviewRegularizationRecord}
        selectedDate={selectedDate}
        onClose={() => setReviewRegularizationRecord(null)}
        onRegularizationHandled={handleRegularizationHandled}
      />

      <LeaveRequestDrawer
        request={reviewLeaveRequest}
        onClose={() => setReviewLeaveRequest(null)}
        onApprove={handleApproveLeave}
        onReject={handleRejectLeave}
      />
    </div>
  )
}
